import type * as THREE from 'three';
import { createBastionMap, createBattle, generateWave, hexRing, stepBattle, type BattleState, type StructureInit } from '@somnhold/sim';
import { BattleView, Post, TableCamera } from '@somnhold/render';
import { localSeason, viewSeason } from '../season.ts';
import { settings } from '../settings.ts';
import { Loop } from './loop.ts';

/** A living diorama behind the menu: towers and ghosts hold a bastion while the camera circles. */
export class Backdrop {
  readonly canvas: HTMLCanvasElement;
  private battle: BattleState;
  private view: BattleView;
  private cam = new TableCamera(window.innerWidth / window.innerHeight);
  private post: Post;
  private loop: Loop;
  private seed = 11;

  constructor(private renderer: THREE.WebGLRenderer) {
    this.canvas = renderer.domElement;
    this.canvas.classList.add('bg');
    this.battle = this.make();
    this.view = new BattleView(this.battle.map, this.battle.seed, { quality: settings.quality, ...viewSeason(this.battle.season) });
    this.post = new Post(renderer, this.view.scene, this.cam.camera, settings.tiltShift);
    this.cam.setDistance(window.innerWidth < window.innerHeight ? 30 : 21, true);
    this.cam.rotate(0, -0.15);
    this.loop = new Loop(
      () => this.tick(),
      (dt) => this.frame(dt),
    );
    window.addEventListener('resize', this.resize);
    this.resize();
  }

  private make(): BattleState {
    const map = createBastionMap(this.seed, 3);
    const structures: StructureInit[] = map.sectors.map((s) => ({ type: 'gate', level: 2, material: 2, q: s.gate.q, r: s.gate.r }));
    const ring = hexRing(5);
    const types = ['ballista', 'arcane', 'catapult', 'belfry', 'beacon', 'ballista'] as const;
    for (let i = 0; i < ring.length; i += 3) structures.push({ type: types[(i / 3) % types.length]!, level: ((i % 2) + 1) as 1 | 2, material: 2, q: ring[i]!.q, r: ring[i]!.r });
    const wave = generateWave({ seed: this.seed, marea: 1, wave: 2, sectors: 3, activityBp: 30000, depth: 'deep', lossPenalty: false });
    return createBattle({
      mode: 'wave',
      seed: this.seed,
      map,
      structures,
      heroes: (['warrior', 'mage', 'cleric'] as const).map((cls, i) => ({ pid: `g${i}`, name: '', cls, level: 4, control: 'ghost' as const, powerBp: 9000, post: i })),
      keepHp: 99999,
      keepMaxHp: 99999,
      marea: 1,
      wave: 2,
      spawns: wave.spawns,
      ...localSeason(Date.now()),
    });
  }

  private tick(): void {
    if (this.battle.result !== 'running' || this.battle.waveT > 2200) {
      this.seed++;
      this.battle = this.make();
    }
    stepBattle(this.battle);
    this.view.sync(this.battle);
  }

  private frame(dt: number): void {
    this.cam.rotate(dt * 0.04);
    this.cam.update(dt);
    this.view.frame(this.cam.camera);
    this.post.render();
  }

  private resize = (): void => {
    const pr = Math.min(window.devicePixelRatio || 1, 1.5);
    this.renderer.setPixelRatio(pr);
    this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    this.cam.resize(window.innerWidth / window.innerHeight);
    this.post.setSize(window.innerWidth, window.innerHeight, pr);
  };

  start(): void {
    // Start a little into the fight so the board isn't empty.
    for (let i = 0; i < 260; i++) stepBattle(this.battle);
    this.loop.start();
  }

  destroy(): void {
    this.loop.stop();
    window.removeEventListener('resize', this.resize);
    this.canvas.classList.remove('bg');
    this.post.dispose();
    this.view.dispose();
  }
}
